import React from 'react';
import { useAdminMode } from '../context/AdminModeContext';
import { useAuth } from '../context/AuthContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import OrganizationPicker from './OrganizationPicker';
import {
  Globe,
  Building2,
  Shield,
  AlertCircle,
} from 'lucide-react';
import { cn } from '../lib/utils';

export const SuperAdminBar = () => {
  const { isSuperAdmin, isBroker, user } = useAuth();
  const {
    operatingMode,
    selectedOrg,
    switchToPlatformMode,
    switchToOrganizationMode,
  } = useAdminMode();

  // Show for super admins and brokers
  if (!isSuperAdmin() && !isBroker()) return null;

  // For brokers, label it differently
  const isUserBroker = isBroker();

  return (
    <div 
      data-testid="super-admin-bar"
      className={cn(
        "border-b px-3 sm:px-4 py-2",
        isUserBroker 
          ? "bg-gradient-to-r from-emerald-100 via-teal-100 to-emerald-100 dark:from-emerald-900/30 dark:via-teal-800/20 dark:to-emerald-900/30 border-emerald-200 dark:border-emerald-500/30"
          : "bg-gradient-to-r from-blue-100 via-sky-100 to-blue-100 dark:from-blue-900/30 dark:via-blue-800/20 dark:to-indigo-900/30 border-blue-200 dark:border-blue-500/30"
      )}
    >
      <div className="flex items-center justify-between gap-2 sm:gap-3 flex-wrap">
        {/* Left side - Mode indicator and context */}
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap min-w-0">
          {/* Mode Badge */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className={cn(
              "text-[10px] sm:text-xs font-medium uppercase tracking-wider hidden xs:inline",
              isUserBroker ? "text-emerald-600 dark:text-emerald-300" : "text-blue-600 dark:text-blue-300"
            )}>Mode:</span>
            <Badge 
              className={cn(
                "px-2 sm:px-3 py-0.5 sm:py-1 font-medium text-[10px] sm:text-xs",
                isUserBroker
                  ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-200 border-emerald-400/50'
                  : 'bg-blue-500/20 text-blue-700 dark:text-blue-200 border-blue-400/50'
              )}
            >
              {operatingMode === 'platform' ? (
                <>
                  <Globe className="w-3 h-3 mr-1 sm:mr-1.5" />
                  <span className="hidden sm:inline">{isUserBroker ? 'My Portfolio' : 'Platform Management'}</span>
                  <span className="sm:hidden">{isUserBroker ? 'Portfolio' : 'Platform'}</span>
                </>
              ) : (
                <>
                  <Building2 className="w-3 h-3 mr-1 sm:mr-1.5" />
                  <span className="hidden sm:inline">Organization View</span>
                  <span className="sm:hidden">Org View</span>
                </>
              )}
            </Badge>
          </div>

          {/* Organization Context (when in org mode) - Hidden on mobile, shown in dropdown */}
          {operatingMode === 'organization' && selectedOrg && (
            <div className="hidden md:flex items-center gap-2 border-l border-blue-300 dark:border-blue-500/30 pl-4">
              <span className="text-xs text-muted-foreground">Context:</span>
              <Badge variant="outline" className="bg-background/50 border-border font-medium text-xs">
                <Building2 className="w-3 h-3 mr-1.5 text-blue-500 dark:text-blue-400" />
                <span className="truncate max-w-[150px]">{selectedOrg.name}</span>
                <span className="ml-1.5 text-[10px] text-muted-foreground">
                  ({selectedOrg.org_type || 'sports'})
                </span>
              </Badge>
            </div>
          )}

          {/* Warning when in org mode without selection */}
          {operatingMode === 'organization' && !selectedOrg && (
            <div className="flex items-center gap-1.5 sm:gap-2 text-yellow-600 dark:text-yellow-400 text-[10px] sm:text-xs">
              <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Select an organization for context</span>
              <span className="sm:hidden">Select org</span>
            </div>
          )}

          {/* Role Badge - Hidden on small mobile */}
          <div className="hidden sm:flex items-center gap-2 border-l border-blue-300 dark:border-blue-500/30 pl-4">
            <Badge variant="secondary" className={cn(
              "text-xs",
              isUserBroker 
                ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-300 border-emerald-400/30"
                : "bg-emerald-500/20 text-emerald-600 dark:text-emerald-300 border-emerald-400/30"
            )}>
              <Shield className="w-3 h-3 mr-1.5" />
              {isUserBroker ? 'Broker' : 'Super Admin'}
            </Badge>
          </div>
        </div>

        {/* Right side - Mode switcher and org selector */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Organization Selector (when in org mode) - Now using floating command palette */}
          {operatingMode === 'organization' && (
            <OrganizationPicker />
          )}

          {/* Mode Switch Buttons */}
          <div className="flex items-center gap-0.5 sm:gap-1 bg-background/30 rounded-lg p-0.5 sm:p-1">
            <Button
              size="sm"
              variant={operatingMode === 'platform' ? 'default' : 'ghost'}
              onClick={switchToPlatformMode}
              className={cn(
                "h-6 sm:h-7 px-2 sm:px-3 text-[10px] sm:text-xs font-medium",
                operatingMode === 'platform' 
                  ? isUserBroker ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              )}
              data-testid="platform-mode-btn"
            >
              <Globe className="w-3 h-3 sm:w-3.5 sm:h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">{isUserBroker ? 'Portfolio' : 'Platform'}</span>
            </Button>
            <Button
              size="sm"
              variant={operatingMode === 'organization' ? 'default' : 'ghost'}
              onClick={() => switchToOrganizationMode()}
              className={cn(
                "h-6 sm:h-7 px-2 sm:px-3 text-[10px] sm:text-xs font-medium",
                operatingMode === 'organization' 
                  ? isUserBroker ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              )}
              data-testid="organization-mode-btn"
            >
              <Building2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">Organization</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminBar;
